
import React, { useEffect, useRef, useState } from 'react'
import { Link, Typography } from '@mui/material'

import DynamicText from 'smartstream-widgets/controls/DynamicText/DynamicText'
import { createStyles, makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  classes?: {
    [K in string]: string
  },
  disclaimer: string,
  footerLink?: string,
  isSingleFixture?: boolean
}

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    width: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    height: 24,
    '--footer-height': '24px',
    justifyContent: 'center',
    '&[data-singlefixture=true] $disclaimerText': {
      padding: `0px 10%`,
    }
  },
  disclaimerText: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'center',
    width: '100%',
    height: 'max-content',
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 8,
    padding: `0px 16%`,
    lineHeight: '140%',
    '& a': {
      color: 'inherit',
      display: 'contents',
      fontSize: 'inherit',
      'white-space': 'nowrap'
    },
    '& span': {
      display: 'contents',
    }
  },
  disclaimerAnimated: {
    '--animation-multiplier': 1,
    '--animation-time': '0s',
    animation: '$first-slide calc(var(--animation-time) * var(--animation-multiplier)) linear 2s, $slide calc(var(--animation-time) * var(--animation-multiplier) + 1s) linear calc(var(--animation-time) * var(--animation-multiplier) + 2s) infinite',
    position: 'absolute',
    '&:hover': {
      animationPlayState: 'paused'
    },
    willChange: 'transform'
  },
  '@keyframes slide': {
    '0%': {
      transform: 'translateY(var(--footer-height))',
    },
    '100%': {
      transform: 'translateY(-100%)',
    }
  },
  '@keyframes first-slide': {
    '0%': {
      transform: 'translateY(0)',
    },
    '100%': {
      transform: 'translateY(-100%)',
    }
  }
}), { name: 'Disclaimer', defaultTheme: getDefaultTheme })

export const Disclaimer = (props: Props) => {
  const classes = useStyles({ classes: props.classes })
  const {
    disclaimer,
    footerLink,
    isSingleFixture = false
  } = props

  const disclaimerRef = useRef<HTMLElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const height = disclaimerRef.current?.clientHeight / 8
    disclaimerRef.current?.style.setProperty('--animation-time', `${height.toFixed(1)}s`)
    rootRef.current?.style.setProperty('--footer-height', `${rootRef.current?.clientHeight}px`)
    setHasOverflow(disclaimerRef.current?.clientHeight > rootRef.current?.clientHeight)
  })

  const Component = footerLink ? Link : Typography as React.ElementType
  return (
    disclaimer ?
      <div className={classes.root} ref={rootRef} data-singlefixture={isSingleFixture} data-testid='disclaimer'>
        <Component
          align='center'
          className={`${classes.disclaimerText} ${hasOverflow ? classes.disclaimerAnimated : ''}`}
          href={footerLink}
          target='_blank'
          underline='hover'
          ref={disclaimerRef}
          component={footerLink ? 'a' : 'span'}
        >
          <DynamicText
            component={'span'}
            classes={{
              textContainer: `${classes.disclaimerText}`
            }}>
            {disclaimer}
          </DynamicText>
        </Component>
      </div>
      : null
  )
}

export default Disclaimer
